// © 2026 AttainIP Engineering
import { useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchEnterprises, fetchEnterpriseEntitlement } from '@/api/queries';
import { api } from '@/api/client';
import { Card } from '@/components/Card';

interface EnterpriseOrg {
  domain: string;
  activated: boolean;
  org_id: string | null;
  ext_id: string | null;
  name: string;
  org_type?: string | null;
  magic_link_enabled: boolean | null;
  user_count: number;
}

// A configured-but-unactivated firm has no org yet — pre-create it so an
// operator can configure allowance/magic-link before its first login
// (RFC-0500 Q#5).
function ActivateButton({ domain }: { domain: string }) {
  const queryClient = useQueryClient();
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setActivating(true);
    setError(null);
    try {
      await api.post('/v1/enterprises/activate', { domain });
      queryClient.invalidateQueries({ queryKey: ['enterprises'] });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const message = detail?.message ?? (typeof detail === 'string' ? detail : null);
      setError(message || 'Failed to activate firm');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleActivate}
        disabled={activating}
        className="text-xs px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50"
      >
        {activating ? 'Activating...' : 'Activate'}
      </button>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

// List page — platform_admin only (RFC-0212 A2). org_admin never routes here
// (they land directly on their own Enterprise Detail).
export function EnterprisesPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprises'],
    queryFn: fetchEnterprises,
  });

  const orgs: EnterpriseOrg[] = data?.data || [];

  // The list endpoint returns org + user_count only; enrich each row with its
  // credit-pool entitlement (allowance/balance/enforcement) client-side —
  // firm counts are small, so an N-way fan-out here is cheap. A firm that
  // isn't activated yet has no org_id, so it has no entitlement to fetch.
  const entitlementQueries = useQueries({
    queries: orgs.map((org) => ({
      queryKey: ['enterprise-entitlement', org.org_id],
      queryFn: () => fetchEnterpriseEntitlement(org.org_id!),
      enabled: org.activated && !!org.org_id,
      retry: false,
    })),
  });

  if (isLoading) return <p className="text-gray-500">Loading enterprises...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">Enterprises ({orgs.length})</h1>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">
          Failed to load enterprises: {(error as Error).message}
        </p>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">Firm</th>
              <th className="pb-2 font-medium">Users</th>
              <th className="pb-2 font-medium">Allowance</th>
              <th className="pb-2 font-medium">Balance</th>
              <th className="pb-2 font-medium">Enforcement</th>
              <th className="pb-2 font-medium">Magic-link</th>
              <th className="pb-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((org, i) => {
              const entitlementQuery = entitlementQueries[i];
              const entitlement = entitlementQuery?.data;
              // ⚠️ AN ACTIVATED FIRM WITH NO ENTITLEMENT ROW IS NOT MERELY
              // UNCONFIGURED — IT CANNOT WORK. `charge_enterprise_pool` raises
              // `EnterprisePoolExhaustedError(required=price, available=0)`
              // when `read_enterprise_pool` returns None (RFC-0501), so every
              // billable workflow fails, and `GET /v1/enterprise/pool` answers
              // 404 `no_pool`. Its users can still sign in, which is what
              // makes it hard to spot.
              //
              // This row used to render '—' for allowance and balance beside a
              // green "Active", i.e. indistinguishable from a healthy firm with
              // a zero balance. On zeta 2026-09-11 two of four firms were in
              // this state (hpdlaw.com, one user; pagewhite.com, none) and the
              // only visible symptom was a 404 in the browser console.
              //
              // `retry: false` above means `isError` settles after the single
              // 404, so this is a definite answer rather than a loading state.
              const noPool =
                !!org.activated && !!org.org_id && entitlementQuery?.isError === true;
              return (
                <tr
                  key={org.org_id ?? org.domain}
                  className={`border-b last:border-0 ${
                    org.activated ? 'hover:bg-gray-50 cursor-pointer' : ''
                  }`}
                  onClick={() => org.activated && navigate(`/enterprises/${org.org_id}`)}
                >
                  <td className="py-2">
                    <div className={org.activated ? 'text-blue-600 hover:underline' : 'text-gray-700'}>
                      {org.name}
                    </div>
                    <div className="text-xs text-gray-400 font-mono">{org.ext_id ?? org.domain}</div>
                  </td>
                  <td className="py-2">{org.user_count}</td>
                  <td className={`py-2 ${noPool ? 'text-red-600' : ''}`}>
                    {entitlement?.monthly_allowance ?? '—'}
                  </td>
                  <td className={`py-2 ${noPool ? 'text-red-600' : ''}`}>
                    {entitlement?.balance ?? '—'}
                  </td>
                  <td className="py-2">
                    {entitlement?.enforcement ? (
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          entitlement.enforcement === 'enforced'
                            ? 'bg-red-50 text-red-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {entitlement.enforcement}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2">
                    {org.activated ? (
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          org.magic_link_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {org.magic_link_enabled ? 'on' : 'off'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-2">
                    {org.activated ? (
                      noPool ? (
                        // NOT "Active". The firm is activated and unusable, and
                        // saying Active here is the part that misleads: an
                        // operator reads the Status column to answer "is this
                        // firm working?", and for a firm with no pool the
                        // answer is no.
                        <span
                          className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700"
                          title="No credit pool provisioned (RFC-0501). Users can sign in, but every billable workflow fails with pool exhausted. Set an entitlement on the firm's detail page."
                        >
                          No credit pool
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Active</span>
                      )
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-yellow-50 text-yellow-700">
                          Not activated
                        </span>
                        <ActivateButton domain={org.domain} />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {orgs.length === 0 && !error && (
          <p className="text-gray-400 text-sm py-4 text-center">No enterprise firms found</p>
        )}
      </Card>
    </div>
  );
}
